// Per-player, per-race-type personal-best pace, persisted in localStorage so the
// solo "Pace Bunny" can chase the player's own best time. Thin browser glue — the
// "is this a new best?" math lives (and is tested) in @shared/pace.
import { foldBest, type BestPace } from "@shared/pace";

const KEY = "bibleracers:personal-best";

type Store = Record<string, BestPace>;

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Store;
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch {
    /* unreadable / blocked — start fresh */
  }
  return {};
}

function write(store: Store): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    /* storage blocked — best just won't persist */
  }
}

/** The player's best pace for a bucket (e.g. `solo:hard`), or null if none yet. */
export function getPersonalBest(bucket: string): BestPace | null {
  return read()[bucket] ?? null;
}

/** Record a finished run, keeping it only if it's a new (faster) best. Returns
 *  the resulting best and whether this run set a record. */
export function recordPersonalBest(
  bucket: string,
  totalMs: number,
  finishLine: number,
): { best: BestPace | null; improved: boolean } {
  const store = read();
  const { best, improved } = foldBest(store[bucket] ?? null, totalMs, finishLine);
  if (improved && best) {
    store[bucket] = best;
    write(store);
  }
  return { best, improved };
}
